import { makeStyles } from '@material-ui/styles';
import {
	Button,
	Checkbox,
	FormControl, FormControlLabel,
	TextField,
} from '@material-ui/core';
import { Check, Remove, Add } from '@material-ui/icons';
import theme from '../../theme';

import { useTranslation } from 'tools/i18n';
import { useContext, useEffect, useState } from 'react';
import Link from 'next/link';
import FastDelivery from '../../../public/images/icons/fast-delivery-thin.svg';
import ProductFamily from '../../../public/images/icons/product-collection.svg';
import HandMade from '../../../public/images/icons/hand-made.svg';
import MeddelaMig from '../../../public/images/icons/meddela-mig.svg';
import { TrackingContext } from '../../providers/tracking';
import { getCategoryBreadcrumb, getProductFamily, getVariants } from '@functions/productOps';

import { ErrorContext } from "../../providers/error";
import parsePrices from "../../../functions/parseSWPrices";
import CampaignBlock from './blocks/campaignBlock';
import AddToWeddingList from '../../products/wishlist/AddToWeddingList';
import useSessionStore from '@services/stores/SessionStore';
import useCartStore from '@services/stores/CartStore';
import CallToActionButton from '@components/products/partials/CallToActionButton';
import LikeButton from '@components/products/wishlist/LikeButton';
import { useRouter } from 'next/router';
import StockIndicator from '@components/products/partials/StockIndicator';
import OutletParentProduct from '@components/page/product/partial/OutletParentProduct';
import PhysicalStoreStockIndicator from '@components/page/product/partial/PhysicalStoreStockIndicator';

const useStyles = makeStyles(
	() => ({
		wrapper: {
			display: 'flex',
			marginTop: 15,
			marginBottom: 15,
			gap: 1,
			'&.soldOut': {
				flexDirection: 'column'
			},
			[theme.breakpoints.down('xs')]: {
				'&:not(.soldOut)': {
					display: 'none'
				}
			}
		},
		wrapperInner: {
			display: 'flex',
			gap: 1
		},
		checkmarks: {
			display: 'flex',
			width: '100%'
		},
		checkmark: {
			display: 'flex',
			alignItems: 'center',
			marginTop: 15,
			marginRight: 16,
			fontSize: 14,
			fontWeight: 300,
			color: theme.palette.primary.lowlight
		},
		checkIcon: {
			fontSize: 20,
			marginRight: 6,
			color: '#24bb81'
		},
		qtyWrapper: {
			display: 'flex',
			alignItems: 'center',
			backgroundColor: '#eaeaea'
		},
		qtyButton: {
			width: 42,
			height: 56,
			minWidth: 'auto',
			textAlign: 'center',
			appearance: 'none',
			cursor: 'pointer',
			[theme.breakpoints.down('sm')]: {
				width: 30,
			},
		},
		qtyIcon: {
			fontSize: 18,
			height: 25,
			weight: 25,
			fontWeight: 400
		},
		qtyInput: {
			height: 56,
			width: 42,
			textAlign: 'center',
			fontSize: 16,
			appearance: 'textfield',
			fontFamily: theme.typography.fontFamily,
			border: 'none',
			outline: 'none',
			backgroundColor: 'transparent',
			'&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
				appearance: 'none',
				margin: 0
			},
			[theme.breakpoints.down('sm')]: {
				width: 30,
			},
		},
		purchaseButton: {
			position: 'relative',
			...theme.button.purchase,
			width: '100%',
			padding: '16px 8px',
			fontSize: 12,
			color: theme.palette.primary.background,
			[theme.breakpoints.up('sm')]: {
				padding: '20px 8px',
				letterSpacing: 2
			},
			'&:before': {
				content: '""',
				height: 1,
				backgroundColor: theme.button.purchasePsuedo.background,
				display: 'block',
				position: 'absolute',
				top: 4,
				left: 0,
				right: 0,
				transition: 'all 0.25s ease'
			},
			'&:after': {
				content: '""',
				height: 1,
				backgroundColor: theme.button.purchasePsuedo.background,
				display: 'block',
				position: 'absolute',
				bottom: 4,
				left: 0,
				right: 0,
				transition: 'all 0.25s ease'
			},
			'&:hover': {
				backgroundColor: theme.button.purchaseHover.backgroundColor,
				'&:before, &:after': {
					left: 4,
					right: 4
				}
			},
		},
		purchaseButtonLabel: {
			flexDirection: 'column',
		},
		purchaseButtonDisabled: {
			color: `${theme.button.purchaseDisabled.color} !important`
		},
		soldOut: {
			width: '100%',
			backgroundColor: theme.button.purchaseDisabled.backgroundColor,
			'&:hover': {
				backgroundColor: theme.button.purchaseDisabled.backgroundColor
			},
		},
		nextShipmentText: {
			width: '100%',
			marginBottom: 10,
			padding: '8px 12px',
			fontSize: 12,
			letterSpacing: 0.5,
			textAlign: 'center',
			backgroundColor: '#f1f1f1',
			'& span': {
				fontWeight: 600
			},
			'&.outdoor': {
				color: "#009640"
			},
			'&.inline': {
				backgroundColor: 'transparent',
				marginBottom: 'auto',
				marginTop: '2px',
				padding: 'unset',
				fontSize: 'inherit',
				letterSpacing: 'inherit',
			}
		},
		subscribeWrapper: {
			marginTop: -10,
			padding: 20,
			backgroundColor: '#f1f1f1'
		},
		subscribeForm: {
			display: 'flex',
			flexDirection: 'row',
			color: theme.palette.primary.background,
			[theme.breakpoints.down('sm')]: {
				flexDirection: 'column'
			}
		},
		subscribeDescription: {
			marginTop: 0,
			marginBottom: 14
		},
		subscribeInputWrapper: {
			marginRight: 15,
			flexGrow: 1,
			[theme.breakpoints.down('sm')]: {
				marginRight: 0
			}
		},
		subscribeInput: {
			backgroundColor: theme.palette.primary.background
		},
		subscribeInputOutline: {
			border: 'none'
		},
		subscribeInputLabel: {
			transform: 'translate(14px, 4px) scale(0.75)'
		},
		subscribeButton: {
			padding: '10px 25px',
			fontSize: 12,
			fontWeight: 600,
			letterSpacing: 2,
			backgroundColor: theme.palette.primary.main,
			borderRadius: 0,
			color: theme.palette.primary.background,
			'&:hover': {
				backgroundColor: '#444444'
			},
			'.xmas &': {
				backgroundColor: theme.palette.primary.main
			},
			'&.success': {
				backgroundColor: '#0e6853',
			},
			[theme.breakpoints.down('sm')]: {
				padding: '14px 25px',
				marginTop: 10
			}
		},
		subscribeNote: {
			display: 'block',
			marginTop: 6,
			fontSize: 11,
			color: '#666',
			'& a': {
				color: 'inherit'
			}
		},
        usps: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: 20,
            marginTop: 15,
            marginBottom: 15,
            [theme.breakpoints.down('sm')]: {
                gap: 22,
            }
        },
        uspItem: {
            display: 'flex',
            alignItems: 'flex-start',
            alignSelf: 'flex-start',
            fontSize: 14,
            fontWeight: 300,
            color: 'rgba(0, 0, 0, 0.87)',
            '& svg': {
                flexShrink: 0,
                width: 15,
                marginRight: 10,
                strokeWidth: 0.5,
                '&.inStockMarker': {
                    '& path': {
                        stroke: 'green !important'
                    }
                },
                '&.productFamily': {
                    width: 22,
                    marginLeft: 0,
                    marginRight: 8,
                    fill: 'transparent',
                    stroke: '#444',
                    strokeWidth: 0.55
                }
            },
            '&:first-of-type': {
                marginTop: '0px !important'
            },
        },
        freeShippingText: {
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            fontSize: 14,
            '& > svg': {
                marginRight: 16
            },
            '&.in-stock > svg': {
                color: 'green'
            },
            '&.order-to-stock > svg': {
                color: 'inherit',
            },
            '& a': {
                color: 'inherit'
            },
        },
		klarnaWidget: {
			width: '100%',
			marginTop: 10,
			marginBottom: 10,
			gridColumn: '1/3',
			'& > div': {
				maxWidth: 'none !important'
			},
			'&::part(osm-container)': {
				maxWidth: 'none',
				minHeight: 0,
				width: '100%'
			},
			'&::part(osm-message),&::part(osm-cta)': {
  				fontSize: 14,
				[theme.breakpoints.down('sm')]: {
					  fontSize: 12
				}
			},
			[theme.breakpoints.down('sm')]: {
				marginTop: 0
			}
		},
		dotdWrapper: {
			position: 'relative',
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			marginTop: 20,
			padding: 25,
			width: '100%',
			backgroundColor: '#000000',
			'&:before': {
				content: '""',
				position: 'absolute',
				top: 20,
				left: 20,
				right: 20,
				bottom: 20,
				zIndex: 1,
				pointerEvents: 'none',
				border: '1px solid transparent',
			},
			[theme.breakpoints.down('sm')]: {
				'&:before': {
					top: 15,
					left: 15,
					right: 15,
					bottom: 15,
				}
			}
		},
		dotdImage: {
			width: '100%',
			marginTop: 20,
			cursor: 'pointer',
			'& img': {
				display: 'block',
				maxWidth: '100%'
			}
		},
		dotdText: {
			marginTop: 5,
			marginBottom: 28,
			color: theme.palette.primary.background
		},
		dotdLink: {
			padding: '8px 20px',
			marginBottom: 20,
			borderRadius: 0,
			backgroundColor: '#f1f1f1',
			backgroundImage: 'linear-gradient(180deg, #f1f1f1, #a0a0a0)',
			color: '#000',
			textTransform: 'none',
		},
		consentSubscribeCheckbox: {
			fontSize: 14,
			marginTop: 10,
		},
	}),
	{ name: 'ProductBuy' }
);

export default function ProductBuy({ product, breadcrumbs, available, stock, productFamilyArticle }) {
	const classes = useStyles();
	const { t } = useTranslation(['general', 'popup', 'product-info']);
	const { config, paymentMethod, currentLanguage, salesChannel } = useSessionStore((state) => state.session);
	const cartActions = useCartStore((state) => state.methods);
	const { actions: trackingActions } = useContext(TrackingContext);
	const {actions: errorActions} = useContext(ErrorContext);
	const [quantity, setQuantity] = useState(1);
	const prices = parsePrices(product.calculatedPrices && product.calculatedPrices.length > 0 ? product.calculatedPrices[0] : product.calculatedPrice);
	const [disabled, setDisabled] = useState(false);
	const [subscribePopup, setSubscribePopup] = useState(true);
	const [subscribeEmail, setSubscribeEmail] = useState('');
	const [subscribeSuccess, setSubscribeSuccess] = useState(false);
	const [subscribeConsent, setSubscribeConsent] = useState(true);
	const [checkoutType, setCheckoutType] = useState(false);

	const customFields = product.translated.customFields;
	const router = useRouter();

	function changeQuantity(quantity) {
		setQuantity(parseInt(quantity, 10));
	}

	useEffect(() => {
        let type = 'klarna';
        if (paymentMethod) {
            if (paymentMethod?.translated?.name !== 'Klarna Checkout') {
                type = 'adyen';
            }
        }

        setCheckoutType(type);
    }, [paymentMethod]);

	function increment() {
		changeQuantity(parseInt(quantity, 10) + 1);
	}

	function decrement() {
		if(quantity > 1) {
			changeQuantity(parseInt(quantity, 10) - 1);
		}
	}

	function onChange(value) {
		changeQuantity(value);
	}

	async function purchase(event) {
		await cartActions.addToCart({productId: product.id, quantity: quantity});
		purchaseTracking();
	}

	function purchaseTracking() {
		let price = product.calculatedPrices && product.calculatedPrices.length > 0 ? product.calculatedPrices[0] : product.calculatedPrice;

		trackingActions.addToCart({
            name: product.translated.name,
            id: product.productNumber,
            price: price.unitPrice,
            discount: Math.abs(prices?.discount?.amount),
            brand: product.manufacturer ? product.manufacturer.translated.name : '',
            category: getCategoryBreadcrumb(breadcrumbs),
            list: { id: '', name: 'add_to_cart_pdp', location: router.asPath},
            variant: getVariants(product),
            quantity: quantity,
            analytics: product?.extensions?.newport?.analytics,
        });
	}

	async function handlePurchase() {
		setDisabled(true);
		await purchase();
		setDisabled(false);
	}

	async function submitProductSubscribe() {
		const country = config?.language?.languageCode.split('-')[1];
		const storeId = salesChannel?.id;

		const subscribe = await fetch('/api/voyado/product/subscribe', {
			method: 'post',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({
				country,
				email: subscribeEmail,
				sku: product.productNumber,
				store: storeId,
				consent: subscribeConsent,
			})
		});

		const result = await subscribe.json();
		if (result.status === 'success') {
			//setSubscribePopup(false);
			setSubscribeSuccess(true);
		}
	}

	function isHandMade() {
		let handmadeArray = product?.properties?.filter((property) => {
			return property?.group?.translated?.customFields?.attributeCode === 'hand_made';
		});

		return handmadeArray?.length > 0;
	}

	return (
        <>
            {available ? (
                <>
                    <div className={classes.wrapper}>
                        <div className={classes.qtyWrapper}>
                            <Button onClick={decrement} className={[classes.qtyButton, 'decrement'].join(' ')}>
                                <Remove className={classes.qtyIcon} />
                            </Button>
                            <input
                                type="number"
                                value={quantity}
                                onChange={event => onChange(event.target.value)}
                                className={classes.qtyInput}
                            />
                            <Button onClick={increment} className={[classes.qtyButton, 'increment'].join(' ')}>
                                <Add className={classes.qtyIcon} />
                            </Button>
                        </div>

						<CallToActionButton
							addLines={true}
							onClick={handlePurchase}
							disabled={disabled}
							isLoading={disabled}
						>
							{t('buy')}
						</CallToActionButton>

						<LikeButton
							show={true}
							productId={product.id}
							hasLightButtons={false}
							size={'large'}
							analyticsName={product?.extensions?.newport?.analytics?.item_name}
						></LikeButton>
                    </div>
                </>
            ) : (
                <>
                    <div className={[classes.wrapper, 'soldOut'].join(' ')}>
						<div className={classes.wrapperInner}>
							<Button
								className={[
									classes.purchaseButton,
									classes.soldOut,
								].join(' ')}
								classes={{ disabled: classes.purchaseButton, label: classes.purchaseButtonLabel }}
								onClick={() => {}}
							>
								{product?.deliveryTime?.customFields?.delivery_time_texts_main_PDP || t('sold-out')}{' '}
							</Button>
							<LikeButton
								show={true}
								productId={product.id}
								hasLightButtons={false}
								size={'large'}
								analyticsName={product?.extensions?.newport?.analytics?.item_name}
							></LikeButton>
						</div>

                        {subscribePopup ? (
                            <div className={classes.subscribeWrapper}>
                                <p className={classes.subscribeDescription}>{t('sold-out-subscribe-description')}</p>
                                <form
                                    onSubmit={e => {
                                        e.preventDefault();
                                        submitProductSubscribe();
                                    }}
                                >
                                    <FormControl className={classes.subscribeForm}>
                                        <TextField
                                            type={'email'}
                                            name={'email'}
                                            variant={'filled'}
                                            className={classes.subscribeInputWrapper}
                                            label={t('popup:intro-email-placeholder')}
                                            value={subscribeEmail}
                                            onChange={event => {
                                                setSubscribeEmail(event.target.value);
                                            }}
                                            inputProps={{ className: classes.subscribeInput }}
                                            InputProps={{
                                                classes: { notchedOutline: classes.subscribeInputOutline },
                                                disableUnderline: true,
                                            }}
                                            required={true}
                                        />
                                        <Button type={'submit'} className={[classes.subscribeButton, subscribeSuccess ? 'success' : ''].join(' ')}>
											{subscribeSuccess ?
												<Check style={{ width: 20, marginLeft: 0 }} />
                                            :
												<>
													{t('sold-out-subscribe')}{' '}
													<MeddelaMig width={20} style={{ marginLeft: 10 }} />
												</>
											}
                                        </Button>
                                    </FormControl>
                                    <FormControlLabel
                                        className={classes.consentSubscribeCheckbox}
                                        control={
                                            <Checkbox
                                                defaultChecked={subscribeConsent}
                                                color="#000"
                                                size="small"
                                                onChange={event => {
                                                    setSubscribeConsent(event.target.checked);
                                                }}
                                            />
                                        }
                                        label={<small>{t('consent-additional-offers')}</small>}
                                    ></FormControlLabel>
                                    <span
                                        className={classes.subscribeNote}
                                        dangerouslySetInnerHTML={{ __html: t('sold-out-subscribe-note') }}
                                    ></span>
                                </form>
                            </div>
                        ) : (
                            ''
                        )}
                    </div>
                </>
            )}

			<AddToWeddingList productId={product.id} colorScheme={'bg-wedding-fresh'}></AddToWeddingList>

            {product.translated.customFields.fast_delivery_available && config.language.locale === 'sv' && stock > 0 ? (
                <div className={[classes.fastDelivery, classes.uspItem].join(' ')}>
                    <span>
                        <FastDelivery width={20} style={{ verticalAlign: 'text-top' }} />
                        Fast Delivery
                    </span>
                    <p className={classes.fastDeliveryText}>I lager och skickas inom 24h under helgfria vardagar</p>
                </div>
            ) : (
                ''
            )}

            <div className={classes.usps}>
				<StockIndicator
					className={classes.stockIndicatorTweaker}
					alternativeName={'pdp'}
					deliveryTime={product.deliveryTime}
                    showIcon={config.language.locale === 'sv'}
				/>

                {config.language.locale === 'sv' && customFields?.store_stock_levels && (
                    <PhysicalStoreStockIndicator
                        productAnalyticsName={product?.extensions?.newport?.analytics?.item_name || ''}
                        propertyIds={product.propertyIds}
                        placement={'pdp'}
                        stockLevels={customFields.store_stock_levels}
                        showIcon={true}
                    />
                )}

                {productFamilyArticle ? (
                    <span className={[classes.freeShippingText, classes.uspItem].join(' ')}>
                        <ProductFamily width={40} className={'productFamily'} />
                        <Link href={'/' + productFamilyArticle.seoUrls[0].seoPathInfo}>
                            <a>
                                {getProductFamily(product).indexOf('Collection') > 0
                                    ? t('general:product-family-collection', {
                                          productFamily: getProductFamily(product),
                                      })
                                    : t('general:product-family', { productFamily: getProductFamily(product) })}
                            </a>
                        </Link>
                    </span>
                ) : (
                    ''
                )}

                {isHandMade() ? (
                    <span className={[classes.freeShippingText, classes.uspItem].join(' ')}>
                        <HandMade width={20} />
                        {t('product-info:is-hand-made')}
                    </span>
                ) : (
                    ''
                )}
            </div>

            <CampaignBlock product={product} prices={prices} productFamily={productFamilyArticle}></CampaignBlock>
			<OutletParentProduct product={product}></OutletParentProduct>
        </>
    );
}
